import React, { ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { DataTableContainer, Table, cn } from '@sib/ui';
import { useElementSize } from '@sib/ui/hooks';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { useTableSettings } from '../hooks';
import {
  useColumnResizeVariables,
  useGetSelectedRowOriginalData,
} from '../hooks';
import {
  MemoizedDataTableBody,
  DataTableNoResultsRow,
  DataTableNoResultsRowProps,
} from './body';
import {
  DataTableFooter,
  DataTablePagination,
  DataTablePaginationProps,
} from './footer';
import { DataTableHeader, DataTableSettings } from './header';

interface DataTableProps<T> {
  rowCount?: number;
  data: T[];
  columns: ColumnDef<T, unknown>[];
  settings: ReturnType<typeof useTableSettings<T>>;
  minPage?: number;
  loading?: boolean;
  withFixedColumnWidth?: boolean;
  withSettingsRow?: boolean;
  preserveSelectedValues?: boolean;
  withDelayedLoading?: boolean;
  hidePagination?: boolean;
  hideFooter?: boolean;
  noResultsPlaceholder?: ReactNode;
  NoResultsContainerProps?: Partial<DataTableNoResultsRowProps>;
  TableContainerProps?: React.HTMLAttributes<HTMLDivElement>;
  TablePaginationProps?: Partial<DataTablePaginationProps>;
  TableProps?: React.HTMLAttributes<HTMLTableElement>;
  withFilters?: boolean;
  onRowClick?: (row: T) => void;
}

type WithSubRows<T> = T & { subRows?: T[] };

export const DataTable = <T,>({
  settings: {
    tableProps,
    pagination,
    onPaginationChange,
    minPage,
    originalRowSelection,
    onOriginalRowSelectionChange,
    disabledColumns,
    exportAction,
    uploadAction,
  },
  rowCount,
  data,
  columns,
  noResultsPlaceholder,
  hideFooter = true,
  hidePagination = false,
  withFixedColumnWidth = false,
  loading = false,
  withSettingsRow = false,
  preserveSelectedValues = false,
  withDelayedLoading = false,
  NoResultsContainerProps,
  TableProps,
  TableContainerProps,
  TablePaginationProps,
  withFilters,
  onRowClick,
}: DataTableProps<T>) => {
  const showNoResults = !loading && data.length === 0;
  const { width, ref } = useElementSize<HTMLTableElement>({
    enabled: showNoResults || (loading && withDelayedLoading),
  });

  const getSelectedRowOriginalData = useGetSelectedRowOriginalData({ data });
  const table = useReactTable({
    data,
    columns,
    rowCount,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableExpanding: true,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row: T) => (row as WithSubRows<T>).subRows ?? [],
    manualFiltering: true,
    ...tableProps,
    onRowSelectionChange: updater => {
      tableProps?.onRowSelectionChange?.(updater);
      if (!preserveSelectedValues) return;

      const valuesToCompare =
        typeof updater === 'function'
          ? updater(tableProps.state?.rowSelection || {})
          : updater;

      const originalData = getSelectedRowOriginalData(
        updater,
        valuesToCompare,
        originalRowSelection
      );

      onOriginalRowSelectionChange?.(originalData);
    },
  });

  const columnSizeVars = useColumnResizeVariables({ table });

  const tableState = table.getState();

  const {
    className: tableClassName,
    style: tableStyle,
    ...restTableProps
  } = TableProps ?? {};

  return (
    <DataTableContainer {...TableContainerProps} ref={ref}>
      {withSettingsRow && (
        <DataTableSettings
          table={table}
          disabledColumns={disabledColumns}
          exportAction={exportAction}
          uploadAction={uploadAction}
        />
      )}
      <Table
        {...restTableProps}
        className={cn(
          'min-w-full border-collapse',
          !withFixedColumnWidth && 'w-max',
          tableClassName
        )}
        style={{
          ...(withFixedColumnWidth && {
            tableLayout: 'fixed',
            width: table.getTotalSize(),
            minWidth: '100%',
          }),
          ...tableStyle,
          ...columnSizeVars,
        }}
        disableWrapper
      >
        <DataTableHeader
          table={table}
          headerGroups={table.getHeaderGroups()}
          hideRounding={withSettingsRow}
          withFilters={withFilters}
          withFixedColumnWidth={withFixedColumnWidth}
        />
        <MemoizedDataTableBody
          TableDelayedLoadingIndicatorProps={{
            width,
          }}
          withDelayedLoading={withDelayedLoading}
          loading={loading}
          isEmpty={showNoResults}
          table={table}
          pageSize={pagination.pageSize || DEFAULT_PAGE_SIZE}
          rowSelection={tableState.rowSelection}
          columnPinning={tableState.columnPinning}
          onRowClick={onRowClick}
          withFixedColumnWidth={withFixedColumnWidth}
          emptyPlaceholder={
            <DataTableNoResultsRow
              colSpan={table.getAllColumns().length}
              {...NoResultsContainerProps}
              width={width}
            >
              {noResultsPlaceholder}
            </DataTableNoResultsRow>
          }
        />
        {!hideFooter && <DataTableFooter table={table} />}
      </Table>
      {data.length > 0 && typeof rowCount === 'number' && !hidePagination && (
        <DataTablePagination
          onChange={onPaginationChange}
          rowCount={rowCount}
          state={pagination}
          minPage={minPage}
          {...TablePaginationProps}
        />
      )}
    </DataTableContainer>
  );
};
